import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { thumbUrl } from '../../../lib/imageUrl';

type PhotoRow = {
  url: string;
  product_id: string | null;
  product_sku: string | null;
  product_name: string | null;
  brand_code: string | null;
  variant_id: string | null;
  variant_finish: string | null;
  variant_sku: string | null;
  is_thumbnail: boolean;
  used_in: 'product' | 'variant' | 'unassigned';
};

type Tab = 'all' | 'unassigned' | 'nuled' | 'lutron';

export function AdminFotos() {
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [uploading, setUploading] = useState(false);
  const [assignTo, setAssignTo] = useState<{ url: string; bytes: number } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Carga todas las fotos: las que están en products.images + variants.primary_image_url + storage sin asignar
  useEffect(() => {
    (async () => {
      setLoading(true);
      const [prods, vars, storageList] = await Promise.all([
        supabase.from('products')
          .select('id, sku, name, brand_code, images')
          .not('images', 'is', null)
          .limit(5000),
        supabase.from('product_variants')
          .select('id, finish, full_sku, primary_image_url, product:products(id, sku, name, brand_code)')
          .not('primary_image_url', 'is', null)
          .limit(10000),
        supabase.storage.from('product-images').list('', { limit: 1000, sortBy: { column: 'created_at', order: 'desc' } }),
      ]);

      const rows: PhotoRow[] = [];
      const usedUrls = new Set<string>();

      // Fotos de products.images
      for (const p of ((prods.data as any[]) ?? [])) {
        const imgs = (p.images as { url: string; is_thumbnail?: boolean }[]) ?? [];
        for (const img of imgs) {
          rows.push({
            url: img.url,
            product_id: p.id, product_sku: p.sku, product_name: p.name, brand_code: p.brand_code,
            variant_id: null, variant_finish: null, variant_sku: null,
            is_thumbnail: !!img.is_thumbnail,
            used_in: 'product',
          });
          usedUrls.add(img.url);
        }
      }

      // Fotos por variante
      for (const v of ((vars.data as any[]) ?? [])) {
        const p = v.product;
        rows.push({
          url: v.primary_image_url,
          product_id: p?.id ?? null, product_sku: p?.sku ?? null, product_name: p?.name ?? null,
          brand_code: p?.brand_code ?? null,
          variant_id: v.id, variant_finish: v.finish, variant_sku: v.full_sku,
          is_thumbnail: false,
          used_in: 'variant',
        });
        usedUrls.add(v.primary_image_url);
      }

      // Storage list: las que están en bucket pero no asignadas
      // Nota: listar el bucket entero es plano (no recursivo) — listamos folders/files top-level
      // Para v2 hacer recursivo o usar storage admin API. Por ahora: muestra los top-level files.
      // El supabase storage API NO devuelve URLs públicas directamente, así que las construimos
      const projectUrl = supabase.storage.from('product-images').getPublicUrl('').data.publicUrl.replace(/\/$/, '');
      for (const f of ((storageList.data as any[]) ?? [])) {
        if (f.id) {
          // archivo
          const u = `${projectUrl}/${f.name}`;
          if (!usedUrls.has(u)) {
            rows.push({
              url: u,
              product_id: null, product_sku: null, product_name: null, brand_code: null,
              variant_id: null, variant_finish: null, variant_sku: null,
              is_thumbnail: false,
              used_in: 'unassigned',
            });
          }
        }
      }

      setPhotos(rows);
      setLoading(false);
    })();
  }, [reloadKey]);

  const counts = useMemo(() => ({
    all: photos.length,
    unassigned: photos.filter(p => p.used_in === 'unassigned').length,
    nuled: photos.filter(p => p.brand_code === 'nuled').length,
    lutron: photos.filter(p => p.brand_code === 'lutron').length,
  }), [photos]);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return photos.filter(p => {
      if (tab === 'unassigned' && p.used_in !== 'unassigned') return false;
      if (tab === 'nuled' && p.brand_code !== 'nuled') return false;
      if (tab === 'lutron' && p.brand_code !== 'lutron') return false;
      if (qn) {
        return (
          (p.product_sku?.toLowerCase().includes(qn) ?? false) ||
          (p.product_name?.toLowerCase().includes(qn) ?? false) ||
          (p.variant_sku?.toLowerCase().includes(qn) ?? false) ||
          (p.variant_finish?.toLowerCase().includes(qn) ?? false)
        );
      }
      return true;
    });
  }, [photos, q, tab]);

  // Bulk upload de fotos al bucket
  const onBulkUpload = async (files: FileList) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      const ts = Date.now() + Math.random().toString(36).slice(2, 8);
      const target = `_unassigned/${ts}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error } = await supabase.storage.from('product-images').upload(target, file, { upsert: false });
      if (error) console.error(`Error subiendo ${file.name}:`, error.message);
    }
    setUploading(false);
    setReloadKey(k => k + 1);
  };

  const deletePhoto = async (photo: PhotoRow) => {
    if (!confirm(`¿Borrar esta foto?${photo.product_sku ? ` Está asignada a ${photo.product_sku}.` : ''}`)) return;
    if (photo.used_in === 'product' && photo.product_id) {
      const { data } = await supabase.from('products').select('images').eq('id', photo.product_id).maybeSingle();
      const newImgs = (((data as any)?.images as any[]) ?? []).filter(i => i.url !== photo.url);
      await supabase.from('products').update({ images: newImgs }).eq('id', photo.product_id);
    } else if (photo.used_in === 'variant' && photo.variant_id) {
      await supabase.from('product_variants').update({ primary_image_url: null }).eq('id', photo.variant_id);
    }
    // Borrar del storage (extraer path desde URL)
    const m = photo.url.match(/\/product-images\/(.+?)(\?|$)/);
    if (m) {
      await supabase.storage.from('product-images').remove([m[1]]);
    }
    setReloadKey(k => k + 1);
  };

  return (
    <AdminContainer
      title="Fotos"
      eyebrow={`Galería · ${photos.length} fotos`}
      action={
        <>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden
                 onChange={e => { if (e.target.files) onBulkUpload(e.target.files); }} />
          <button style={btnPrimary} onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Subiendo…' : '+ Subir fotos'}
          </button>
        </>
      }
    >
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        <TabBtn active={tab === 'all'} onClick={() => setTab('all')}>Todas ({counts.all})</TabBtn>
        <TabBtn active={tab === 'unassigned'} onClick={() => setTab('unassigned')}>Sin asignar ({counts.unassigned})</TabBtn>
        <TabBtn active={tab === 'nuled'} onClick={() => setTab('nuled')}>NULED ({counts.nuled})</TabBtn>
        <TabBtn active={tab === 'lutron'} onClick={() => setTab('lutron')}>Lutron ({counts.lutron})</TabBtn>
      </div>

      <input
        placeholder="Buscar por SKU, nombre, acabado de variante…"
        value={q}
        onChange={e => setQ(e.target.value)}
        style={{
          width: '100%', padding: '10px 14px', border: `1px solid ${theme.border}`,
          fontSize: 14, outline: 'none', color: theme.text, background: theme.bg,
          fontFamily: theme.font.sans, marginBottom: 16, boxSizing: 'border-box',
        }}
      />

      <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
        {loading ? 'Cargando…' : `${filtered.length} de ${photos.length} fotos`}
      </div>

      {/* Drop zone */}
      <DropZone onFiles={onBulkUpload} uploading={uploading} />

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 12,
        marginTop: 16,
      }}>
        {filtered.slice(0, 200).map((p, i) => (
          <PhotoCard
            key={p.url + i}
            photo={p}
            onDelete={() => deletePhoto(p)}
            onAssign={() => setAssignTo({ url: p.url, bytes: 0 })}
          />
        ))}
      </div>

      {filtered.length > 200 && (
        <div style={{ marginTop: 12, fontSize: 12, color: theme.textMuted, textAlign: 'center' }}>
          Mostrando 200 de {filtered.length}. Refina con búsqueda o tabs.
        </div>
      )}

      {assignTo && (
        <AssignModal
          url={assignTo.url}
          onClose={() => setAssignTo(null)}
          onAssigned={() => { setAssignTo(null); setReloadKey(k => k + 1); }}
        />
      )}
    </AdminContainer>
  );
}

function PhotoCard({ photo, onDelete, onAssign }: {
  photo: PhotoRow;
  onDelete: () => void;
  onAssign: () => void;
}) {
  return (
    <div style={{ border: `1px solid ${theme.border}`, background: theme.bg }}>
      <div style={{
        aspectRatio: '1', backgroundColor: theme.bgImage,
        position: 'relative', overflow: 'hidden',
      }}>
        <img
          src={thumbUrl(photo.url, 250)}
          alt={photo.product_sku ?? 'foto'}
          loading="lazy"
          decoding="async"
          style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'contain',
          }}
        />
        {photo.is_thumbnail && (
          <span style={{
            position: 'absolute', top: 4, left: 4,
            background: theme.accent, color: theme.brand,
            fontSize: 9, fontWeight: 700, padding: '2px 6px', textTransform: 'uppercase',
          }}>★ Principal</span>
        )}
        {photo.used_in === 'unassigned' && (
          <span style={{
            position: 'absolute', top: 4, right: 4,
            background: '#FEE2E2', color: '#991B1B',
            fontSize: 9, fontWeight: 700, padding: '2px 6px', textTransform: 'uppercase',
          }}>Sin asignar</span>
        )}
      </div>
      <div style={{ padding: 8 }}>
        {photo.product_sku ? (
          <>
            <div style={{ fontSize: 10, fontFamily: theme.font.mono, color: theme.textMuted }}>
              {photo.product_sku}
            </div>
            <div style={{ fontSize: 11, color: theme.brand, fontWeight: 500, lineHeight: 1.3, marginTop: 2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {photo.product_name}
            </div>
            {photo.variant_finish && (
              <div style={{ fontSize: 9, color: theme.accentDark, fontWeight: 700, marginTop: 2, textTransform: 'uppercase' }}>
                Variante: {photo.variant_finish}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 11, color: theme.textMuted, fontStyle: 'italic' }}>
            Foto huérfana
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
          {photo.used_in === 'unassigned' ? (
            <button onClick={onAssign} style={miniBtnFull}>Asignar</button>
          ) : (
            <a href={photo.url} target="_blank" rel="noopener noreferrer" style={miniBtnLink}>Abrir</a>
          )}
          <button onClick={onDelete} style={{ ...miniBtn, color: '#B91C1C' }}>×</button>
        </div>
      </div>
    </div>
  );
}

function DropZone({ onFiles, uploading }: { onFiles: (f: FileList) => void; uploading: boolean }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={e => {
        e.preventDefault();
        setHover(false);
        if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
      }}
      style={{
        padding: 24, textAlign: 'center',
        border: `2px dashed ${hover ? theme.accent : theme.border}`,
        background: hover ? theme.bgFooter : theme.bgSoft,
        color: theme.textMuted, fontSize: 13, fontFamily: theme.font.sans,
        transition: 'all 0.2s ease',
      }}
    >
      {uploading ? '⏳ Subiendo fotos…' : '📁 Arrastra fotos aquí para subir múltiples a la vez'}
    </div>
  );
}

// ============ Modal asignar foto a producto/variante ============
function AssignModal({ url, onClose, onAssigned }: {
  url: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [variants, setVariants] = useState<any[]>([]);
  const [target, setTarget] = useState<'product' | string>('product');  // 'product' o variant id
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setMatches([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      const { data } = await supabase.from('products')
        .select('id, sku, name, brand_code')
        .or(`sku.ilike.%${q}%,name.ilike.%${q}%`)
        .limit(15);
      if (alive) setMatches((data as any[]) ?? []);
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    if (!selected) { setVariants([]); return; }
    supabase.from('product_variants')
      .select('id, full_sku, sku_suffix, finish, color_temperature_k, wattage_w')
      .eq('product_id', selected.id).order('position')
      .then(({ data }) => setVariants((data as any[]) ?? []));
  }, [selected]);

  const assign = async () => {
    if (!selected) return;
    setSaving(true);
    if (target === 'product') {
      const { data } = await supabase.from('products').select('images').eq('id', selected.id).maybeSingle();
      const imgs = ((data as any)?.images as any[]) ?? [];
      const newImgs = [...imgs, { url, is_thumbnail: imgs.length === 0, sort_order: imgs.length }];
      await supabase.from('products').update({ images: newImgs }).eq('id', selected.id);
    } else {
      await supabase.from('product_variants').update({ primary_image_url: url }).eq('id', target);
    }
    setSaving(false);
    onAssigned();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        padding: 32, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
      }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
          Asignar foto
        </div>
        <h2 style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 400, color: theme.brand, margin: '4px 0 16px' }}>
          ¿A qué producto / variante?
        </h2>

        <div style={{
          aspectRatio: '3/1', backgroundImage: `url(${url})`,
          backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
          backgroundColor: theme.bgImage, marginBottom: 16,
        }} />

        {!selected ? (
          <>
            <input
              placeholder="Buscar SKU o nombre…"
              value={q}
              onChange={e => setQ(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px', border: `1px solid ${theme.border}`,
                fontSize: 14, outline: 'none', color: theme.text, background: theme.bg,
                fontFamily: theme.font.sans, boxSizing: 'border-box',
              }}
              autoFocus
            />
            <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto', border: `1px solid ${theme.border}` }}>
              {matches.map(m => (
                <button
                  key={m.id}
                  onClick={() => setSelected(m)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 12px', border: 'none', borderBottom: `1px solid ${theme.border}`,
                    background: 'transparent', cursor: 'pointer', fontFamily: theme.font.sans,
                  }}
                >
                  <div style={{ fontSize: 11, fontFamily: theme.font.mono, color: theme.textMuted }}>{m.sku}</div>
                  <div style={{ fontSize: 13, color: theme.brand }}>{m.name}</div>
                </button>
              ))}
              {matches.length === 0 && q.length >= 2 && (
                <div style={{ padding: 24, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
                  Sin resultados
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{
              padding: 12, background: theme.bgFooter, border: `1px solid ${theme.border}`, marginBottom: 12,
            }}>
              <div style={{ fontSize: 11, color: theme.textMuted, fontFamily: theme.font.mono }}>{selected.sku}</div>
              <div style={{ fontSize: 14, color: theme.brand, fontWeight: 600 }}>{selected.name}</div>
              <button onClick={() => setSelected(null)} style={{
                marginTop: 6, background: 'transparent', border: 'none', color: theme.textMuted,
                fontSize: 11, cursor: 'pointer', textDecoration: 'underline',
              }}>← cambiar producto</button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6, fontWeight: 700, textTransform: 'uppercase' }}>
                Asignar a:
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, cursor: 'pointer' }}>
                <input type="radio" name="target" checked={target === 'product'} onChange={() => setTarget('product')} />
                <span style={{ color: theme.brand, fontSize: 13 }}>📷 Foto del producto base (galería)</span>
              </label>
              {variants.map(v => (
                <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, cursor: 'pointer' }}>
                  <input type="radio" name="target" checked={target === v.id} onChange={() => setTarget(v.id)} />
                  <span style={{ color: theme.brand, fontSize: 13 }}>
                    🎨 Variante: {v.finish ?? v.sku_suffix ?? v.full_sku}
                    {v.color_temperature_k && ` · ${v.color_temperature_k}K`}
                    {v.wattage_w && ` · ${v.wattage_w}W`}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand,
            padding: '10px 18px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, cursor: 'pointer', fontFamily: theme.font.sans,
          }}>Cancelar</button>
          <button onClick={assign} disabled={!selected || saving} style={{
            ...btnPrimary,
            opacity: (!selected || saving) ? 0.5 : 1,
            cursor: (!selected || saving) ? 'not-allowed' : 'pointer',
          }}>{saving ? 'Asignando…' : 'Asignar foto'}</button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent',
      color: active ? theme.brand : theme.textMuted,
      border: 'none',
      borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      padding: '12px 16px',
      fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans, marginBottom: -1,
    }}>{children}</button>
  );
}

const btnPrimary: React.CSSProperties = {
  background: theme.accent, color: theme.brand, border: 'none',
  padding: '10px 18px', fontSize: 12, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const miniBtn: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand,
  cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '4px 8px',
  fontFamily: theme.font.sans, flex: 1,
};
const miniBtnFull: React.CSSProperties = {
  ...miniBtn, background: theme.brand, color: theme.textOnBrand, border: 'none',
};
const miniBtnLink: React.CSSProperties = {
  ...miniBtn, textAlign: 'center', textDecoration: 'none', display: 'inline-block',
};
